<template>
  <div>
    <v-btn
      block
      outlined
      class="hazard-trigger"
      :color="selectedFactors.length ? 'primary' : 'grey darken-1'"
      @click="openDialog"
    >
      <template v-if="selectedFactors.length">
        <span
          v-for="factor in selectedFactors"
          :key="factor.number"
          class="hazard-number mr-2"
          :style="{ backgroundColor: factor.color }"
        >
          <v-icon x-small dark>{{ factor.icon }}</v-icon>
          {{ factor.number }}
        </span>
        <span class="text-truncate">
          {{ $t("riskAssessment.advanced.editHazardFactors") }}
        </span>
      </template>
      <template v-else>
        <v-icon left small>mdi-alert-outline</v-icon>
        {{ $t("riskAssessment.advanced.chooseHazardFactor") }}
      </template>
    </v-btn>

    <div v-if="selectedFactors.length" class="mt-2">
      <div
        v-for="factor in selectedFactors"
        :key="`s${factor.number}`"
        class="hazard-summary"
      >
        <span
          class="hazard-number mr-2"
          :style="{ backgroundColor: factor.color }"
          >{{ factor.number }}</span
        >
        <span>
          {{ factor.title }}
          <span v-if="pointsOf(factor, risk.hazardPoints).length" class="grey--text">
            ({{ pointsOf(factor, risk.hazardPoints).join(", ") }})
          </span>
        </span>
      </div>
    </div>

    <v-dialog v-model="open" max-width="900" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center flex-nowrap">
          <v-btn v-if="activeFactor" icon class="mr-2" @click="activeFactor = null">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span v-if="activeFactor" class="text-truncate">
            {{ activeFactor.number }}. {{ activeFactor.title }}
          </span>
          <span v-else>{{ $t("riskAssessment.advanced.hazardFactor") }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-row v-if="!activeFactor" dense>
            <v-col
              v-for="factor in factors"
              :key="factor.number"
              cols="6"
              sm="4"
              md="3"
            >
              <v-card
                outlined
                class="hazard-tile"
                :class="{ 'hazard-tile--active': isDraftSelected(factor) }"
                @click="activeFactor = factor"
              >
                <div
                  class="hazard-tile__icon"
                  :style="{ backgroundColor: factor.color }"
                >
                  <v-icon large dark>{{ factor.icon }}</v-icon>
                  <span class="hazard-tile__number">{{ factor.number }}</span>
                  <v-icon
                    v-if="isDraftSelected(factor)"
                    class="hazard-tile__check"
                    dark
                  >
                    mdi-check-circle
                  </v-icon>
                </div>
                <div class="hazard-tile__title">
                  {{ factor.title }}
                  <div
                    v-if="pointsOf(factor, draftPoints).length"
                    class="caption grey--text"
                  >
                    {{ pointsOf(factor, draftPoints).join(", ") }}
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
          <div v-else>
            <v-checkbox
              v-for="point in activeFactor.points"
              :key="point.code"
              :input-value="draftPoints.includes(point.code)"
              :label="`${point.code} ${point.label}`"
              hide-details
              dense
              class="mt-1"
              @change="togglePoint(activeFactor, point.code, $event)"
            />
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn
            v-if="draftFactors.length"
            text
            color="error"
            @click="clearDraft"
          >
            {{ $t("riskAssessment.advanced.removeHazardFactor") }}
          </v-btn>
          <v-spacer />
          <v-btn text @click="open = false">{{ $t("general.cancel") }}</v-btn>
          <v-btn color="primary" @click="apply">
            {{ $t("general.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  HAZARD_FACTORS,
  hazardFactorByNumber,
  factorsOfRisk,
} from "@/data/hazardFactors";

export default {
  name: "risk-hazard-factor-dialog",
  props: {
    risk: { type: Object, required: true },
  },
  data: () => ({
    open: false,
    activeFactor: null,
    draftFactors: [],
    draftPoints: [],
  }),
  computed: {
    factors() {
      return HAZARD_FACTORS;
    },
    selectedFactors() {
      return factorsOfRisk(this.risk)
        .map((n) => hazardFactorByNumber(n))
        .filter(Boolean);
    },
  },
  methods: {
    pointsOf(factor, points) {
      const codes = factor.points.map((p) => p.code);
      return codes.filter((code) => (points || []).includes(code));
    },
    isDraftSelected(factor) {
      return this.draftFactors.includes(factor.number);
    },
    openDialog() {
      this.draftFactors = factorsOfRisk(this.risk);
      this.draftPoints = [...(this.risk.hazardPoints || [])];
      this.activeFactor = null;
      this.open = true;
    },
    toggleFactor(factor, on) {
      if (on) {
        if (!this.isDraftSelected(factor)) {
          this.draftFactors = [...this.draftFactors, factor.number];
        }
      } else {
        const codes = factor.points.map((p) => p.code);
        this.draftFactors = this.draftFactors.filter((n) => n !== factor.number);
        this.draftPoints = this.draftPoints.filter((c) => !codes.includes(c));
      }
    },
    togglePoint(factor, code, on) {
      if (on) {
        if (!this.draftPoints.includes(code)) {
          this.draftPoints = [...this.draftPoints, code];
        }
        this.toggleFactor(factor, true);
      } else {
        this.draftPoints = this.draftPoints.filter((c) => c !== code);
        if (!this.pointsOf(factor, this.draftPoints).length) {
          this.toggleFactor(factor, false);
        }
      }
    },
    clearDraft() {
      this.draftFactors = [];
      this.draftPoints = [];
    },
    apply() {
      const factors = [...this.draftFactors].sort((a, b) => a - b);
      const order = HAZARD_FACTORS.flatMap((f) => f.points.map((p) => p.code));
      const points = order.filter(
        (code) =>
          this.draftPoints.includes(code) &&
          factors.includes(Number(code.split(".")[0]))
      );
      this.$set(this.risk, "hazardFactors", factors);
      this.$set(this.risk, "hazardFactor", factors[0] || null);
      this.$set(this.risk, "hazardPoints", points);
      this.open = false;
    },
  },
};
</script>

<style scoped>
.hazard-trigger {
  justify-content: flex-start;
  text-transform: none;
  letter-spacing: normal;
}
.hazard-trigger >>> .v-btn__content {
  max-width: 100%;
}
.hazard-number {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #fff;
  border-radius: 4px;
  padding: 1px 6px;
  font-weight: 700;
  font-size: 12px;
}
.hazard-summary {
  display: flex;
  align-items: baseline;
  font-size: 0.875rem;
  padding: 2px 0;
}
.hazard-tile {
  height: 100%;
  cursor: pointer;
  transition: box-shadow 0.15s;
}
.hazard-tile:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.hazard-tile--active {
  border: 2px solid var(--v-primary-base) !important;
}
.hazard-tile__icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 72px;
  border-radius: 4px 4px 0 0;
}
.hazard-tile__number {
  position: absolute;
  top: 4px;
  left: 8px;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}
.hazard-tile__check {
  position: absolute;
  top: 4px;
  right: 6px;
}
.hazard-tile__title {
  padding: 8px;
  font-size: 13px;
  line-height: 1.3;
}
</style>
